// Fusao de tres vias: base (ultima versao sincronizada) x local (agora) x
// remoto (servidor). Funcoes puras, sem I/O, sem store, sem rede.
//
// Invariantes que este arquivo garante:
// - Nada e apagado por falta de informacao: sem base, tudo do remoto e criacao.
// - Apagado de um lado e alterado do outro preserva a alteracao.
// - Empate de conflito e resolvido de forma deterministica, igual nos dois
//   dispositivos: quem executa a fusao nunca influencia o resultado.

// Campos de controle da sincronizacao; nunca participam da fusao campo a campo.
const META_FIELDS = ["updatedAt", "updatedBy"];

export function deepEqual(a, b) {
  if (a === b) return true;
  if (a === null || b === null || a === undefined || b === undefined) return a === b;
  if (typeof a !== "object" || typeof b !== "object") return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a)) {
    if (a.length !== b.length) return false;
    return a.every((item, i) => deepEqual(item, b[i]));
  }
  const ka = Object.keys(a).sort();
  const kb = Object.keys(b).sort();
  if (ka.length !== kb.length) return false;
  if (!ka.every((k, i) => k === kb[i])) return false;
  return ka.every((k) => deepEqual(a[k], b[k]));
}

// Representacao estavel usada como ultimo criterio de desempate. Ordena as
// chaves para nao depender da ordem de insercao.
function stableString(value) {
  if (value === undefined) return " undefined";
  if (value === null || typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stableString).join(",")}]`;
  const keys = Object.keys(value).sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${stableString(value[k])}`).join(",")}}`;
}

// Verdadeiro quando o lado local vence o conflito. Usa apenas dados presentes
// nos proprios documentos, para que os dois dispositivos cheguem ao mesmo
// resultado.
function localWins(localMeta, remoteMeta, localValue, remoteValue) {
  const lt = Number(localMeta.updatedAt) || 0;
  const rt = Number(remoteMeta.updatedAt) || 0;
  if (lt !== rt) return lt > rt;
  const lb = localMeta.updatedBy || "";
  const rb = remoteMeta.updatedBy || "";
  if (lb !== rb) return lb > rb;
  return stableString(localValue) >= stableString(remoteValue);
}

function metaOf(item) {
  return {
    updatedAt: item && item.updatedAt !== undefined ? item.updatedAt : 0,
    updatedBy: item && item.updatedBy !== undefined ? item.updatedBy : "",
  };
}

// Fusao campo a campo de dois objetos com a mesma identidade. `base` pode ser
// null (documento nunca sincronizado antes).
export function mergeFields(base, local, remote) {
  const localMeta = metaOf(local);
  const remoteMeta = metaOf(remote);
  const keys = new Set([...Object.keys(local || {}), ...Object.keys(remote || {})]);
  META_FIELDS.forEach((f) => keys.delete(f));

  const result = {};
  keys.forEach((key) => {
    const hasLocal = local && key in local;
    const hasRemote = remote && key in remote;
    const b = base ? base[key] : undefined;
    const l = hasLocal ? local[key] : undefined;
    const r = hasRemote ? remote[key] : undefined;

    if (deepEqual(l, r)) {
      if (hasLocal || hasRemote) result[key] = hasLocal ? l : r;
      return;
    }

    const localChanged = !base || !deepEqual(l, b);
    const remoteChanged = !base || !deepEqual(r, b);

    let chosen;
    let present;
    if (localChanged && !remoteChanged) {
      chosen = l;
      present = hasLocal;
    } else if (remoteChanged && !localChanged) {
      chosen = r;
      present = hasRemote;
    } else if (localWins(localMeta, remoteMeta, l, r)) {
      chosen = l;
      present = hasLocal;
    } else {
      chosen = r;
      present = hasRemote;
    }
    if (present) result[key] = chosen;
  });

  // O carimbo mais recente sobrevive, para que a proxima fusao compare contra
  // o estado mais novo dos dois.
  const winnerIsLocal = localWins(localMeta, remoteMeta, local, remote);
  const winner = winnerIsLocal ? localMeta : remoteMeta;
  if (winner.updatedAt) result.updatedAt = winner.updatedAt;
  if (winner.updatedBy) result.updatedBy = winner.updatedBy;
  return result;
}

// Chave de tarefa sem `id`, vinda de instalacao anterior a WP03. Derivada do
// conteudo para que a mesma tarefa nos dois lados nao vire duas.
function fallbackKey(item, index) {
  return `anon:${stableString({
    text: item ? item.text : null,
    listId: item ? item.listId : null,
    time: item ? item.time : null,
    index,
  })}`;
}

function indexBy(list, keyOf) {
  const map = new Map();
  (list || []).forEach((item, index) => {
    const key = keyOf(item, index);
    if (!map.has(key)) map.set(key, item);
  });
  return map;
}

// Fusao de uma lista de itens identificados por chave. Ordem local preservada;
// itens so remotos entram no fim mantendo a ordem relativa deles.
export function mergeKeyedList(base, local, remote, keyOf) {
  const baseMap = indexBy(base, keyOf);
  const localMap = indexBy(local, keyOf);
  const remoteMap = indexBy(remote, keyOf);
  const hasBase = Array.isArray(base);

  const resolve = (key) => {
    const b = baseMap.has(key) ? baseMap.get(key) : null;
    const l = localMap.has(key) ? localMap.get(key) : null;
    const r = remoteMap.has(key) ? remoteMap.get(key) : null;

    if (l && r) return mergeFields(b, l, r);
    if (l && !r) {
      // Sem base, ausencia no remoto e "ainda nao chegou la", nunca apagado.
      if (!hasBase || !b) return l;
      return deepEqual(stripMeta(l), stripMeta(b)) ? null : l;
    }
    if (!l && r) {
      if (!hasBase || !b) return r;
      return deepEqual(stripMeta(r), stripMeta(b)) ? null : r;
    }
    return null;
  };

  const merged = [];
  const seen = new Set();
  (local || []).forEach((item, index) => {
    const key = keyOf(item, index);
    if (seen.has(key)) return;
    seen.add(key);
    const value = resolve(key);
    if (value) merged.push(value);
  });
  (remote || []).forEach((item, index) => {
    const key = keyOf(item, index);
    if (seen.has(key)) return;
    seen.add(key);
    const value = resolve(key);
    if (value) merged.push(value);
  });
  return merged;
}

function stripMeta(item) {
  if (!item || typeof item !== "object") return item;
  const copy = { ...item };
  META_FIELDS.forEach((f) => delete copy[f]);
  return copy;
}

export function taskKey(task, index) {
  return task && task.id ? task.id : fallbackKey(task, index);
}

export function mergeTaskLists(base, local, remote) {
  return mergeKeyedList(base, local, remote, taskKey);
}

export function mergeCustomLists(base, local, remote) {
  return mergeKeyedList(base, local, remote, (item, index) =>
    item && item.listId !== undefined ? `list:${item.listId}` : `anon:${index}`
  );
}

// Mapas simples chave -> valor: repeating_events_by_date e o objeto de config.
// Chave ausente num lado so e removida se estava na base e nao mudou no outro.
export function mergeMaps(base, local, remote) {
  const hasBase = base && typeof base === "object";
  const keys = new Set([...Object.keys(local || {}), ...Object.keys(remote || {})]);
  const result = {};
  keys.forEach((key) => {
    const inLocal = local && key in local;
    const inRemote = remote && key in remote;
    const b = hasBase && key in base ? base[key] : undefined;
    if (inLocal && inRemote) {
      if (deepEqual(local[key], remote[key])) {
        result[key] = local[key];
      } else if (hasBase && deepEqual(remote[key], b)) {
        result[key] = local[key];
      } else if (hasBase && deepEqual(local[key], b)) {
        result[key] = remote[key];
      } else {
        result[key] = mergeScalarConflict(local, remote, local[key], remote[key]);
      }
      return;
    }
    const value = inLocal ? local[key] : remote[key];
    const other = inLocal ? remote : local;
    const removedThere = hasBase && key in base && other && !(key in other);
    if (!removedThere) {
      result[key] = value;
      return;
    }
    // Removido de um lado: so some se o outro lado nao alterou o valor.
    if (!deepEqual(value, b)) result[key] = value;
  });
  return result;
}

function mergeScalarConflict(localDoc, remoteDoc, localValue, remoteValue) {
  return localWins(metaOf(localDoc), metaOf(remoteDoc), localValue, remoteValue)
    ? localValue
    : remoteValue;
}

// Ponto unico de entrada usado pelo syncEngine.
export function mergeDocument(kind, base, local, remote) {
  switch (kind) {
    case "todo_lists":
      return mergeTaskLists(base, local, remote);
    case "custom_lists":
      return mergeCustomLists(base, local, remote);
    case "repeating_events":
    case "repeating_events_by_date":
    case "config":
      return mergeMaps(base, local, remote);
    default:
      throw new Error(`unknown sync document kind: ${kind}`);
  }
}

export default {
  deepEqual,
  mergeFields,
  mergeKeyedList,
  mergeTaskLists,
  mergeCustomLists,
  mergeMaps,
  mergeDocument,
  taskKey,
};
